import React, { useMemo, useState } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import useAdvancedSearchData from 'src/serverProxy/useAdvancedSearchData';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import _capitalize from 'lodash/capitalize';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useLatestCumulativeReviewWeekId } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';

const reviewPeriodOptions = [
  { id: 'lifetime', label: 'Cumulative' },
  { id: 'weekly', label: 'Weekly' }
];

const mainMetricOptions = [
  { id: 'ratingVolume', label: 'Rating Volume' },
  { id: 'rating', label: 'Rating' }
];

const fields = {
  lifetime: {
    ratingVolume: [{ name: RatingsAndReviewMetric.CHILD_LIFETIME_RATING_VOLUME }],
    rating: [
      { name: RatingsAndReviewMetric.CHILD_LIFETIME_RATING_VOLUME },
      { name: RatingsAndReviewMetric.WEIGHTED_CHILD_LIFETIME_RATING },
      { name: RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING }
    ]
  },
  weekly: {
    ratingVolume: [{ name: RatingsAndReviewMetric.WEEKLY_RATING_VOLUME }],
    rating: [
      { name: RatingsAndReviewMetric.WEEKLY_RATING_VOLUME },
      { name: RatingsAndReviewMetric.WEIGHTED_CHILD_WEEKLY_RATING },
      { name: RatingsAndReviewMetric.WEEKLY_RATING }
    ]
  }
};

const responseKeyMap = {
  ratingVolume: 'RatingVolume_sum',
  rating: 'Rating_computed'
};

enum SubtitleDisplayName {
  rating = 'rating',
  ratingVolume = 'rating volume',
  weekly = '',
  lifetime = 'cumulative'
}

/**
 * Double Dropdown for (Cumulative/Weekly) and (Rating/RatingVolume) by Category/ SubCategory graph for ratings tab.
 */

const ReviewRatingDoubleDropdownByCategoryChart = ({ groupByField }: { groupByField: string }) => {
  const [selectedMainMetricField, setSelectedMainMetricField] = useState('ratingVolume');
  const [reviewPeriod, setReviewPeriod] = useState('lifetime');
  const groupByKey = groupByField === 'subCategoryId' ? 'subcategory' : 'category';
  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const { data, isLoading } = useAdvancedSearchData({
    fields: fields[reviewPeriod][selectedMainMetricField],
    groupByField,
    indexName: BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2,
    requestId: `Beacon-ratings-and-reviews-double-dropdown-by-${groupByField}`,
    shouldFetchComparison: true,
    requestBuilder(builder, compare) {
      if (reviewPeriod === 'lifetime') {
        builder.modifyAggregation((aggBuilder) => {
          aggBuilder.replaceConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId);
          return aggBuilder;
        });
        builder.replaceConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId);
        if (compare) {
          builder.modifyAggregation((aggBuilder) => {
            aggBuilder.replaceConditionRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek);
            return aggBuilder;
          });
          builder.replaceConditionRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek);
        }
      }
      return builder;
    }
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    data.forEach((datum) => {
      const categoryObj = datum?.metadata?.[groupByKey] ?? datum?.comparisonMetadata?.[groupByKey];

      const formattedCategoryObj = {
        name: categoryObj?.name,
        id: categoryObj?.id,
        type: categoryObj?.categoryType?.toLocaleLowerCase()
      };
      let responseKey = `${reviewPeriod}${responseKeyMap[selectedMainMetricField]}_value`;

      if (responseKey === 'lifetimeRatingVolume_sum_value') {
        responseKey = 'childLifetimeRatingVolume_sum_value';
      }

      if (responseKey === 'weeklyRatingVolume_sum_value') {
        responseKey = 'childWeeklyRatingVolume_sum_value';
      }

      const currentDatum = datum?.values?.[responseKey] ?? 0;
      const comparisonDatum = datum?.comparisonValues?.[responseKey] ?? 0;

      if (categoryObj && categoryObj.name) {
        comparisonData.push([categoryObj.name, comparisonDatum]);
        currentData.push([categoryObj.name, currentDatum]);
        categoryData.push(formattedCategoryObj);
      }
    });
    return { currentData, categoryData, comparisonData };
  }, [data, groupByKey, reviewPeriod, selectedMainMetricField]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        try {
          const label = `${_capitalize(SubtitleDisplayName[reviewPeriod])} ${_capitalize(
            SubtitleDisplayName[selectedMainMetricField]
          )}`;
          return saveBarChartAsDelimitedData(parsedData.currentData, parsedData.comparisonData, {
            fileTitle: `Rating and Reviews - ${label}`,
            metricLabel: label
          });
        } catch (error) {
          console.error(`Ratings and Review fail`);
        }
        return Promise.resolve();
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the ${SubtitleDisplayName[reviewPeriod]} ${SubtitleDisplayName[selectedMainMetricField]} for ${entityPhrase} `}
            {taxonomyText} in each {groupByKey}
            {reviewPeriod === 'lifetime'
              ? '.'
              : ` during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      subtitleSX={{ marginTop: '8px' }}
      includeSummaryInfo={false}
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      title={
        <Flex alignItems="center" gap={6}>
          <SlDropdownMenu
            buttonVariant="title"
            options={reviewPeriodOptions}
            selectedId={reviewPeriod}
            onChange={(option) => setReviewPeriod(option.id)}
            defaultLabel="Cumulative"
            open={false}
          />

          <SlDropdownMenu
            buttonVariant="title"
            options={mainMetricOptions}
            selectedId={selectedMainMetricField}
            onChange={(option) => setSelectedMainMetricField(option.id)}
            defaultLabel="Rating Volume"
            open={false}
          />
          <Text variant="h4">by {_capitalize(groupByKey)}</Text>
        </Flex>
      }
    >
      {isLoading ? (
        <TopEntityChartV2Loading disableLegendSkeleton />
      ) : (
        <TopEntityChartV2
          chartSeries={[
            {
              type: 'column',
              data: parsedData.comparisonData,
              categories: parsedData.categoryData,
              metricType: selectedMainMetricField === 'ratingVolume' ? METRICTYPE.VOLUME : METRICTYPE.DECIMAL
            },
            {
              type: 'column',
              data: parsedData.currentData,
              categories: parsedData.categoryData,
              metricType: selectedMainMetricField === 'ratingVolume' ? METRICTYPE.VOLUME : METRICTYPE.DECIMAL
            }
          ]}
        />
      )}
    </BeaconChartWithLegend>
  );
};

export default ReviewRatingDoubleDropdownByCategoryChart;
